import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    afDateUtils, LogCategory, LogLevel, LogSystem, LogSystemLevelUtils, RequestLogCategory, RequestLogSystem, TimePeriod
} from '@myapp/features/admin/models';
import { LogSystemServiceGrid } from '@myapp/features/admin/services';
import { UtcToLocalPipe } from '@myapp/modules/fwcore/pipes';
import { LoggingService } from '@myapp/modules/fwcore/services';
import { ControlResizerOptions, ControlResizerService } from '@mysrc/services/framework';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { ComboBoxModule, MultiSelectModule } from '@progress/kendo-angular-dropdowns';
import {  GridDataResult, GridModule } from "@progress/kendo-angular-grid";
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { State } from '@progress/kendo-data-query';
import { Observable, Subject } from "rxjs";
import { takeUntil } from 'rxjs/operators';
import { LogSystemDetailComponent } from '../../components/log-system/log-system-detail/log-system-detail.component';

@Component({
  selector: 'app-page-log-system',
  standalone: true,
  imports: [GridModule, LabelModule, ComboBoxModule, FormsModule, TextBoxModule, MultiSelectModule, ButtonModule
    ,   NgClass, NgIf, LogSystemDetailComponent, AsyncPipe, UtcToLocalPipe],
  templateUrl: './page-log-system.component.html',
  styleUrls: ['./page-log-system.component.scss']
})
export class PageLogSystemComponent implements AfterViewInit, OnInit, OnDestroy {

  /*-------------------------- public properties / methods --------------------*/

  public view!: Observable<GridDataResult>;

  timePeriods: TimePeriod[] = Object.values(TimePeriod);
  public selectedTimePeriod?: TimePeriod = this.timePeriods[0];

  severityLevels: LogLevel[] = Object.values(LogLevel);
  public selectedSeverityLevel?: LogLevel;

  logCategories: LogCategory[] = [];

  public state: State = {
    skip: 0,
    take: 15,
    sort: []
  };

  public req: RequestLogSystem = {
    skip: 0,
    take: 15
  };

  public sizes = [10, 20, 50];
  selectedRow: LogSystem | undefined;
  heightGrid!: number;

  protected destroy$ = new Subject();
  protected viewInit: boolean = false;

  protected filterCategoryIds: string[] = [];
  protected filterSeverities: string[] = [];

  protected filterSource?: string | null = null;
  protected filterLoggedBy?: string | null = null;

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected service: LogSystemServiceGrid
    , protected controlResizer: ControlResizerService
    , protected logService: LoggingService
  ) {
    this.view = service;
  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit(): void {
    this.setFilters();
    this.service.query(this.req);

    let reqCat: RequestLogCategory = {};

    this.logService.getLogSystemCategories$(reqCat)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {

        if (res.data) {
          this.logCategories = res.data;
        }
      });

    this.viewInit = true;
    this.sizeChange();
  }

  ngAfterViewInit(): void {


  }

  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  --------------------------*/

  @HostListener('window:resize', ['$event'])
  public onResize(event: Event) {

    this.sizeChange();
  }

  protected onStateChange(state: State): void {

    this.state = state;

    let sevFilter: string[] = [];

    sevFilter = [...this.filterSeverities];

    let sevIssues: string[] = [LogLevel.Warning, LogLevel.Error, LogLevel.Critical];

    if ((sevFilter).includes(LogLevel.Issues)) {
      const uniqueSet = new Set([...this.filterSeverities, ...sevIssues]);
      uniqueSet.delete(LogLevel.Issues);

      sevFilter = Array.from(uniqueSet);
    }

    this.req = {
      ...this.req
      , skip: state.skip
      , take: state.take
      , categoryIds: this.filterCategoryIds
      , severities: sevFilter
      , loggedBy: this.filterLoggedBy
      , source: this.filterSource
    };

    this.service.query(this.req);

  }

  protected onDetailClick(dataItem: LogSystem): void {

    this.selectedRow = dataItem;
  }

  protected onSearchClick(): void {

    this.searchData();
  }

  protected onResetClick(): void {

    this.selectedTimePeriod = this.timePeriods[0];
    this.searchData();
  }

  protected onCancel(): void {

    this.selectedRow = undefined;
  }

  /*--------------------------- internal functions  --------------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();
      options.offsetTopHeight = 58;
      options.offsetBottomHeight = 75;

      this.heightGrid = this.controlResizer.calculateHeight(options);
    }
  }

  protected setFilters(): void {

    let sevFilter: string[] = [];

    sevFilter = [...this.filterSeverities];

    let sevIssues: string[] = [LogLevel.Warning, LogLevel.Error, LogLevel.Critical];

    if ((sevFilter).includes(LogLevel.Issues)) {
      const uniqueSet = new Set([...this.filterSeverities, ...sevIssues]);
      uniqueSet.delete(LogLevel.Issues);

      sevFilter = Array.from(uniqueSet);
    }

    this.req = {
      ...this.req
      , fromDate: afDateUtils.generateStartRange(this.selectedTimePeriod)
      , toDate: new Date()
      , categoryIds: this.filterCategoryIds
      , severities: sevFilter
      , loggedBy: this.filterLoggedBy
      , source: this.filterSource
    };
  }

  protected searchData(): void {

    this.setFilters();
    this.resetSkip();

    this.service.query(this.req);
  }

  protected resetSkip(): void {

    this.state.skip = this.req.skip = 0;
  }

  protected getSeverityClass(level: LogLevel | undefined | null): string {
    return LogSystemLevelUtils.getClass(level);
  }

}
